import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axios from "axios";

import { CustomTextBox } from "../atoms";
import { AuthSliceAction } from "../../redux/slice/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { authDetailsInitializeAction, lockProfileAction, loginAction, unlockProfileAction } from "../../redux/actions/authAction";
import { getTenantId } from "../../util/common-functions";
import { LoginUser } from "../../typings/interfaces/responses/LoginUser";
import CustomModal from "../common/custom-modal/custom-modal";
import useNfcReader from "../../hooks/useNfcReader";
import { VERIFY_NFC_UNLOCK } from "../../configs/apiConfig";
import UserAvatar from "../common/user-avatar/UserAvatar";

const emailToId = (email: string) =>
  email.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);


interface InactivityTrackerProps {
  timeoutInSeconds?: number;
  onInactive?: () => void;
  onActive?: () => void;
}

const InactivityTracker: React.FC<InactivityTrackerProps> = ({
  timeoutInSeconds = /*180*/ 600,
  onInactive,
  onActive,
}) => {
  const [selectedUser, setSelectedUser] = useState<LoginUser>();
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const dispatch = useAppDispatch();
  const { locked, user, token, loginUsers, loading } = useAppSelector(
    (state) => state.auth
  );
  const tenantId = getTenantId();
  const scannedCard = useNfcReader();
  const [nfcSubmitting, setNfcSubmitting] = useState(false);

  useEffect(() => {
    setSelectedUser({
      email: user?.email || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    });
  }, [user]);

  // NFC card unlock
  useEffect(() => {
    if (!locked || !scannedCard || nfcSubmitting) return;

    setNfcSubmitting(true);
    axios
      .post(
        VERIFY_NFC_UNLOCK,
        { nfcCardId: scannedCard },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      )
      .then((res) => {
        if (res.data?.status === "SUCCESS") {
          // Re-validate account/session status before returning to the lobby —
          // the account may have been locked or the session revoked while idle.
          dispatch(authDetailsInitializeAction()).then(() => {
            dispatch(unlockProfileAction());
          });
        } else {
          setPasswordError(res.data?.message || "NFC card not recognised.");
        }
      })
      .catch(() => setPasswordError("NFC card not recognised."))
      .finally(() => setNfcSubmitting(false));
  }, [scannedCard]);

  useEffect(() => {
    if (token) {
      let inactivityTimer: NodeJS.Timeout;

      const handleUserActivity = () => {
        setIsActive(true);
        clearTimeout(inactivityTimer);

        inactivityTimer = setTimeout(() => {
          setIsActive(false);

          dispatch(lockProfileAction());
          if (onInactive) {
            onInactive();
          }
        }, timeoutInSeconds * 1000);
      };

      // Attach the event listeners
      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("touchmove", handleUserActivity);

      // Set up initial timer
      inactivityTimer = setTimeout(() => {
        setIsActive(false);
        dispatch(lockProfileAction());
        if (onInactive) {
          onInactive();
        }
      }, timeoutInSeconds * 1000);

      // Clean up the event listeners
      return () => {
        window.removeEventListener("mousemove", handleUserActivity);
        window.removeEventListener("touchmove", handleUserActivity);
        clearTimeout(inactivityTimer);
      };
    }
  }, [onInactive, timeoutInSeconds, token]);

  useEffect(() => {
    // Notify when the component becomes active
    if (isActive && onActive) {
      onActive();
    }

    if (!isActive) {
      setPassword("");
    }
  }, [isActive, onActive]);

  const handleOnPasswordChange = (e: any) => {
    setPassword(e.target.value);
  };

  const handleOnClick = () => {
    if (password !== "") {
      dispatch(
        loginAction({
          email: selectedUser?.email || "",
          password: password,
          whiteLabel: tenantId,
        })
      ).then((res) => {
        const payload: any = res.payload;
        setPasswordError(payload?.response?.message);
      });
    }
  };

  const handleOnEnter = (e: any) => {
    if (e.key === "Enter") {
      handleOnClick();
    }
  };

  const handleOnClickProfile = (user: LoginUser) => {
    setSelectedUser(user);
  };

  return (
    <Box>
      {!locked ? (
        <></>
      ) : (
        <Box
          sx={{
            position: "fixed",
            backgroundColor: "rgba(0,0,0,0.2)",
            backdropFilter: "blur(1.07142857rem)",
            width: "100vw",
            height: "100vh",
            zIndex: 9999999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CustomModal
            title=""
            openModal={locked}
            onClose={() => {}}
            width="23.2142857rem"
            height="40rem"
            mode="popup"
            backdropProps={{
              sx: {
                backgroundColor: "rgba(0,0,0,0.2)",
                backdropFilter: "blur(1.07142857rem)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                whiteSpace: "nowrap",
                maxHeight: "38rem",
              }}
            >
              <UserAvatar
                name={`${selectedUser?.firstName ?? ""} ${selectedUser?.lastName ?? ""}`}
                id={emailToId(selectedUser?.email ?? "")}
                size={80}
              />
              <Box sx={{ my: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "1.42857143rem !important" }}
                >
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "0.0714285714rem solid #ccc",
                  borderRadius: "0.285714286rem",
                  display: "flex",
                  alignItems: "center",
                  height: "3rem",
                }}
              >
                <CustomTextBox
                  onKeyDown={handleOnEnter}
                  error={passwordError !== ""}
                  type="password"
                  onChange={handleOnPasswordChange}
                  placeholder="Passcode"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderWidth: "0",
                      border: "none !important",
                      margin: "1rem",
                    },
                  }}
                />
                <IconButton
                  disabled={loading}
                  onClick={handleOnClick}
                  aria-label="delete"
                  size="medium"
                  sx={{
                    width: "2.85714286rem",
                    height: "2.85714286rem",
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex" }}>
                      <CircularProgress size="2rem" />
                    </Box>
                  ) : (
                    <ArrowForwardIcon fontSize="inherit" />
                  )}
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                mt: { xs: "1rem", sm: "2rem", md: "7rem" },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Stack
                direction="row"
                flexWrap={"wrap"}
                justifyContent={"center"}
              >
                {loginUsers
                  .filter((item) => item.email !== selectedUser?.email)
                  .slice(0, 6)
                  .map((item, i) => {
                    return (
                      <IconButton
                        key={i}
                        aria-label="delete"
                        size="medium"
                        sx={{
                          width: "4.57142857rem",
                          height: "4.57142857rem",
                        }}
                        onClick={() => handleOnClickProfile(item)}
                      >
                        <UserAvatar
                          name={`${item.firstName} ${item.lastName}`}
                          id={emailToId(item.email)}
                          size={40}
                        />
                      </IconButton>
                    );
                  })}
              </Stack>
            </Box>
          </CustomModal>
        </Box>
      )}
    </Box>
  );
};

export default InactivityTracker;
